import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { activityFeedApi, type ActivityFeedItem } from '@/plugins/api'

export const useActivityFeedStore = defineStore('activityFeed', () => {
  // State
  const items = ref<ActivityFeedItem[]>([])
  const unreadCount = ref(0)
  const loading = ref(false)
  const hasMore = ref(true)
  const isOpen = ref(false)
  const currentPage = ref(1)
  const pageSize = 20
  const expandedGroups = ref<Set<string>>(new Set())
  const groupedItems = ref<Map<string, ActivityFeedItem[]>>(new Map())

  // Getters
  const unreadItems = computed(() => items.value.filter((item) => !item.isRead))

  // Actions
  async function fetchFeed(reset = false) {
    if (loading.value) return

    if (reset) {
      currentPage.value = 1
      items.value = []
      hasMore.value = true
    }

    loading.value = true
    try {
      const response = await activityFeedApi.getFeed({
        page: currentPage.value,
        limit: pageSize,
      })

      if (reset) {
        items.value = response.data.items
      } else {
        items.value.push(...response.data.items)
      }

      unreadCount.value = response.data.unreadCount
      hasMore.value = response.data.hasMore
      currentPage.value++
    } catch (error) {
      console.error('Failed to fetch activity feed:', error)
    } finally {
      loading.value = false
    }
  }

  async function fetchUnreadCount() {
    try {
      const response = await activityFeedApi.getUnreadCount()
      unreadCount.value = response.data.count
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
    }
  }

  async function loadMore() {
    if (!hasMore.value || loading.value) return
    await fetchFeed(false)
  }

  async function markAsRead(ids: string[]) {
    try {
      await activityFeedApi.markAsRead(ids)

      // Update local state
      items.value = items.value.map((item) => {
        if (ids.includes(item.id)) {
          return { ...item, isRead: true }
        }
        return item
      })

      // Decrement unread count
      const markedCount = ids.filter((id) =>
        items.value.find((item) => item.id === id && !item.isRead)
      ).length
      unreadCount.value = Math.max(0, unreadCount.value - markedCount)
    } catch (error) {
      console.error('Failed to mark as read:', error)
    }
  }

  async function markAllAsRead() {
    try {
      await activityFeedApi.markAllAsRead()

      // Update local state
      items.value = items.value.map((item) => ({ ...item, isRead: true }))
      unreadCount.value = 0
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  async function expandGroup(parentId: string) {
    if (expandedGroups.value.has(parentId)) {
      expandedGroups.value.delete(parentId)
      return
    }

    try {
      const response = await activityFeedApi.getGroupedEvents(parentId)
      groupedItems.value.set(parentId, response.data.items)
      expandedGroups.value.add(parentId)
    } catch (error) {
      console.error('Failed to load grouped events:', error)
    }
  }

  function togglePanel() {
    isOpen.value = !isOpen.value
    if (isOpen.value && items.value.length === 0) {
      fetchFeed(true)
    }
  }

  function closePanel() {
    isOpen.value = false
  }

  function openPanel() {
    isOpen.value = true
    if (items.value.length === 0) {
      fetchFeed(true)
    }
  }

  // Handle WebSocket events
  function handleNewActivity(activity: ActivityFeedItem) {
    console.debug('[ActivityFeedStore] handleNewActivity:', activity.id, activity.title)
    // Add to the beginning of the list
    const existingIndex = items.value.findIndex((item) => item.id === activity.id)
    if (existingIndex >= 0) {
      // Update existing item (e.g., group count increased)
      items.value[existingIndex] = activity
    } else {
      items.value.unshift(activity)
    }
    // Increment unread count if not read
    if (!activity.isRead) {
      unreadCount.value++
    }
  }

  function handleUnreadCountUpdate(count: number) {
    console.debug('[ActivityFeedStore] handleUnreadCountUpdate:', count)
    unreadCount.value = count
  }

  // Handle read status sync from other devices
  function handleReadStatusSync(readIds: string[]) {
    console.debug('[ActivityFeedStore] handleReadStatusSync:', readIds)
    items.value = items.value.map((item) => {
      if (readIds.includes(item.id)) {
        return { ...item, isRead: true }
      }
      return item
    })
  }

  // Initialize (WebSocket handles real-time updates via handleNewActivity/handleUnreadCountUpdate)
  function init() {
    fetchUnreadCount()
  }

  return {
    // State
    items,
    unreadCount,
    loading,
    hasMore,
    isOpen,
    expandedGroups,
    groupedItems,

    // Getters
    unreadItems,

    // Actions
    fetchFeed,
    fetchUnreadCount,
    loadMore,
    markAsRead,
    markAllAsRead,
    expandGroup,
    togglePanel,
    closePanel,
    openPanel,
    handleNewActivity,
    handleUnreadCountUpdate,
    handleReadStatusSync,
    init,
  }
})
